import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  X, Upload, Loader2, FileText, Image as ImageIcon, Download, Trash2, Eye, FileBadge2, Clock,
} from 'lucide-react';
import { toast } from '../components/ui/sonner';
import MissingDocsChecklist from './MissingDocsChecklist';
import { FuelReceiptUploader } from './FuelReceiptUploader';
import { Fuel } from 'lucide-react';
import { putDocBlob, enqueueOperation, listDocBlobsForLoad, removeDocBlob, removeOperation } from '../db/offlineStore';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOC_KINDS = [
  { value: 'pod', label: 'Proof of Delivery (POD)' },
  { value: 'bol', label: 'Bill of Lading (BOL)' },
  { value: 'lumper', label: 'Lumper Receipt' },
  { value: 'scale_ticket', label: 'Scale Ticket' },
  { value: 'rate_confirmation', label: 'Rate Confirmation' },
  { value: 'misc', label: 'Misc / Other' },
];

const KIND_LABEL = Object.fromEntries(DOC_KINDS.map((k) => [k.value, k.label]));

const isImage = (mime) => (mime || '').startsWith('image/');

const fmtBytes = (n) => {
  if (!n) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const fmtTime = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const LoadDocumentsModal = ({ load, role, currentUserId, open, onClose }) => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [docKind, setDocKind] = useState('pod');
  const [notes, setNotes] = useState('');
  const [preview, setPreview] = useState(null); // { url, mime, name }
  const [showFuel, setShowFuel] = useState(false);
  const [fuelSaved, setFuelSaved] = useState(0);
  const fileRef = useRef(null);

  const isStaff = role === 'super_admin' || role === 'company_admin' || role === 'dispatcher';

  const fetchDocs = async () => {
    if (!load?.id) return;
    setLoading(true);
    // Always pull any offline-queued blobs for this load — they should render
    // as "Pending upload" rows alongside server-confirmed docs.
    let pending = [];
    try {
      const blobs = await listDocBlobsForLoad(load.id);
      pending = blobs.map((b) => ({
        id: `pending-${b.opId}`,
        _op_id: b.opId,
        _pending: true,
        doc_kind: b.docKind,
        file_name: b.fileName,
        mime_type: b.mimeType,
        size_bytes: b.size,
        notes: b.notes,
        created_at: b.createdAt,
      }));
    } catch { /* IDB unavailable — non-fatal */ }

    try {
      const r = await axios.get(`${API}/loads/${load.id}/documents`, { withCredentials: true });
      setDocs([...pending, ...(r.data || [])]);
    } catch (e) {
      // Offline: still show pending items so the driver knows what's queued.
      if (pending.length > 0) {
        setDocs(pending);
      } else {
        toast.error(e?.response?.data?.detail || 'Failed to load documents');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) fetchDocs();
  }, [open, load?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open || !load) return null;

  const handleUpload = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large (max 10MB)');
      return;
    }
    setUploading(true);

    // Generate an opId per attempt so retries after a network blip are
    // idempotent on the server. Offline path saves the blob to IDB + queues;
    // online path uses the same opId so the two flows are indistinguishable
    // to the backend.
    const opId = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : `op-${Date.now()}-${Math.random().toString(36).slice(2)}`;

    if (!navigator.onLine) {
      // OFFLINE: persist blob to IndexedDB, enqueue operation, refresh view.
      try {
        await putDocBlob({
          opId, loadId: load.id, docKind, notes,
          file, fileName: file.name, mimeType: file.type,
        });
        await enqueueOperation({
          opId, kind: 'load_document', loadId: load.id,
          payload: { doc_kind: docKind, notes },
        });
        toast.success('Saved offline — will upload automatically when back online');
        setNotes('');
        if (fileRef.current) fileRef.current.value = '';
        fetchDocs();
      } catch (e) {
        toast.error('Could not save offline — please try again when back online.');
      } finally {
        setUploading(false);
      }
      return;
    }

    const fd = new FormData();
    fd.append('file', file);
    fd.append('doc_kind', docKind);
    if (notes) fd.append('notes', notes);
    fd.append('operation_id', opId);
    try {
      await axios.post(`${API}/loads/${load.id}/documents`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Document uploaded');
      setNotes('');
      if (fileRef.current) fileRef.current.value = '';
      fetchDocs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Delete ${doc.file_name}?`)) return;
    // Pending offline docs live only in IndexedDB — cancel by removing the
    // blob + queued op, no server call.
    if (doc._pending) {
      try {
        await removeDocBlob(doc._op_id);
        await removeOperation(doc._op_id);
        toast.success('Pending upload cancelled');
        fetchDocs();
      } catch {
        toast.error('Could not cancel pending upload');
      }
      return;
    }
    try {
      await axios.delete(`${API}/load-documents/${doc.id}`, { withCredentials: true });
      toast.success('Document deleted');
      fetchDocs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const previewDoc = async (doc) => {
    // Fetch as blob to preserve cookie-auth, then show in modal viewer
    try {
      const r = await axios.get(`${API}/load-documents/${doc.id}/file`, {
        withCredentials: true, responseType: 'blob',
      });
      const url = URL.createObjectURL(r.data);
      setPreview({ url, mime: doc.mime_type, name: doc.file_name });
    } catch (e) {
      toast.error('Preview failed');
    }
  };

  const downloadDoc = async (doc) => {
    try {
      const r = await axios.get(`${API}/load-documents/${doc.id}/file`, {
        withCredentials: true, responseType: 'blob',
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.file_name || 'document';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      toast.error('Download failed');
    }
  };

  const canDelete = (doc) => isStaff || String(doc.uploaded_by) === String(currentUserId);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/50" onClick={onClose}>
      <div
        className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="load-documents-modal"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div>
            <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Work Sans, sans-serif' }}>
              <FileBadge2 className="w-5 h-5 text-[#002FA7]" />
              Load documents
            </h2>
            <p className="text-xs text-slate-500 mt-0.5" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              {load.load_ref || load.id.slice(-6)} • {load.pickup_location} → {load.delivery_location}
            </p>
          </div>
          <button onClick={onClose} data-testid="load-docs-close" className="p-1 hover:bg-slate-100 rounded">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>

        {/* Compliance checklist (POD / BOL / Rate Con) */}
        <MissingDocsChecklist
          docs={docs}
          onSelectKind={(kind) => {
            setDocKind(kind);
            // Scroll uploader into view + focus file input so the workflow is one click
            setTimeout(() => {
              try {
                fileRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                fileRef.current?.focus();
              } catch { /* noop */ }
            }, 50);
          }}
        />

        {/* Upload section */}
        <div className="px-6 py-4 bg-slate-50 border-b border-slate-100" data-testid="load-docs-upload-section">
          <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            Upload a document
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
            <select
              data-testid="load-docs-kind"
              value={docKind}
              onChange={(e) => setDocKind(e.target.value)}
              className="px-3 py-2 border border-slate-300 rounded-md text-sm bg-white focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
              style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
            >
              {DOC_KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
            </select>
            <input
              data-testid="load-docs-notes"
              type="text"
              placeholder="Optional notes (e.g. signed by receiver)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="px-3 py-2 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
              style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
            />
          </div>
          <div className="flex items-center gap-3">
            <input
              ref={fileRef}
              type="file"
              data-testid="load-docs-file-input"
              accept="application/pdf,image/jpeg,image/png,image/webp"
              onChange={(e) => handleUpload(e.target.files?.[0])}
              className="text-xs text-slate-600 file:mr-2 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-[#002FA7] file:text-white file:font-semibold file:hover:bg-[#00247D]"
              style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
              disabled={uploading}
            />
            {uploading && <Loader2 className="w-4 h-4 animate-spin text-[#002FA7]" />}
          </div>
          <p className="text-[11px] text-slate-500 mt-1.5" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            PDF, JPG, PNG or WEBP up to 10MB. Documents are kept per-load with full upload history.
          </p>
        </div>

        {/* Fuel receipt → shared Expenses record (never a plain load document, so it can't be double-charged) */}
        <div className="px-6 py-3 border-b border-slate-100" data-testid="load-docs-fuel-section">
          <button type="button" data-testid="load-docs-fuel-toggle" onClick={() => setShowFuel(!showFuel)} className="inline-flex items-center gap-2 text-sm font-semibold text-[#002FA7] hover:underline">
            <Fuel className="w-4 h-4" /> {showFuel ? 'Hide fuel receipt upload' : 'Add a fuel receipt for this trip'}
          </button>
          <p className="text-[11px] text-slate-500 mt-0.5" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            Fuel receipts go to the company's fuel expense queue linked to this load, the driver and the truck. An administrator approves them before any payroll deduction.
            {fuelSaved > 0 && <span className="text-emerald-700 font-semibold"> {fuelSaved} fuel receipt{fuelSaved > 1 ? 's' : ''} submitted for this load.</span>}
          </p>
          {showFuel && (
            <div className="mt-3">
              <FuelReceiptUploader role={role} loadId={load.id} driverUserId={load.driver_id ? String(load.driver_id) : ''} truckId={load.truck_asset_id || ''} onCancel={() => setShowFuel(false)}
                onSaved={() => { setFuelSaved((n) => n + 1); setShowFuel(false); }} />
            </div>
          )}
        </div>

        {/* Doc list */}
        <div className="px-6 py-4 overflow-y-auto" data-testid="load-docs-list">
          {loading ? (
            <div className="flex items-center justify-center py-6 text-slate-500 text-sm">
              <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
            </div>
          ) : docs.length === 0 ? (
            <p className="py-10 text-center text-sm text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              No documents uploaded yet.
            </p>
          ) : (
            <ul className="space-y-2">
              {docs.map((d) => (
                <li
                  key={d.id}
                  data-testid={`load-doc-${d.id}`}
                  className={`flex items-center gap-3 px-3 py-2.5 border rounded-md transition-colors ${
                    d._pending
                      ? 'border-amber-300 bg-amber-50/40 hover:border-amber-400'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className={`w-9 h-9 rounded-md flex items-center justify-center flex-shrink-0 ${d._pending ? 'bg-amber-200/60' : 'bg-[#002FA7]/10'}`}>
                    {d._pending
                      ? <Clock className="w-4 h-4 text-amber-700" />
                      : isImage(d.mime_type)
                        ? <ImageIcon className="w-4 h-4 text-[#002FA7]" />
                        : <FileText className="w-4 h-4 text-[#002FA7]" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-slate-900 truncate" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                      {KIND_LABEL[d.doc_kind] || d.doc_kind} — {d.file_name}
                      {d._pending && (
                        <span
                          data-testid={`load-doc-pending-badge-${d._op_id}`}
                          className="ml-2 inline-flex items-center gap-1 rounded-full bg-amber-200 text-amber-900 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5"
                        >
                          Pending upload
                        </span>
                      )}
                    </p>
                    <p className="text-[11px] text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                      {fmtBytes(d.size_bytes)} •{' '}
                      {d._pending
                        ? `Saved offline ${fmtTime(d.created_at)} — will upload when back online`
                        : `Uploaded ${fmtTime(d.created_at)} by ${d.uploader_name} (${d.uploader_role})`}
                    </p>
                    {d.notes && (
                      <p className="text-[11px] text-slate-600 mt-1 italic" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                        “{d.notes}”
                      </p>
                    )}
                  </div>
                  {!d._pending && (
                    <button
                      type="button"
                      onClick={() => previewDoc(d)}
                      data-testid={`load-doc-preview-${d.id}`}
                      title="Preview"
                      className="p-2 rounded hover:bg-slate-100 text-slate-600 hover:text-[#002FA7]"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                  )}
                  {!d._pending && (
                    <button
                      type="button"
                      onClick={() => downloadDoc(d)}
                      data-testid={`load-doc-download-${d.id}`}
                      title="Download"
                      className="p-2 rounded hover:bg-slate-100 text-slate-600 hover:text-[#002FA7]"
                    >
                      <Download className="w-4 h-4" />
                    </button>
                  )}
                  {(canDelete(d) || d._pending) && (
                    <button
                      type="button"
                      onClick={() => handleDelete(d)}
                      data-testid={`load-doc-delete-${d.id}`}
                      title={d._pending ? 'Cancel pending upload' : 'Delete'}
                      className="p-2 rounded hover:bg-red-50 text-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Preview viewer */}
      {preview && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center p-6 bg-slate-950/80"
          onClick={() => { URL.revokeObjectURL(preview.url); setPreview(null); }}
          data-testid="load-doc-preview-overlay"
        >
          <div className="relative w-full max-w-4xl max-h-[90vh] flex flex-col bg-white rounded-lg overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-2 border-b border-slate-100 bg-white">
              <p className="text-sm font-semibold text-slate-900 truncate" style={{ fontFamily: 'Work Sans, sans-serif' }}>{preview.name}</p>
              <button onClick={() => { URL.revokeObjectURL(preview.url); setPreview(null); }} className="p-1 hover:bg-slate-100 rounded" data-testid="load-doc-preview-close">
                <X className="w-4 h-4" />
              </button>
            </div>
            {isImage(preview.mime) ? (
              <img src={preview.url} alt={preview.name} className="max-h-[85vh] object-contain" />
            ) : (
              <iframe src={preview.url} title={preview.name} className="w-full h-[80vh]" />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LoadDocumentsModal;
